import {useEffect, useRef, useCallback, useState} from "react";
import io from "socket.io-client";

import {crud} from "../crudRequests";

// `tenant` joins the tenant-scoped room alongside the legacy bare-pid one.
// Patient ids are NOT unique across tenants — 593 are shared between
// havenpointhealth, canadianhealthlabs and internal — so a room named for the
// bare pid is joined by two different people. New server events publish only to
// `t:<db>:<pid>`; the legacy room stays until every emitter has moved.
export function useSocket(url, room, dispatch, tenant) {
  const socketRef = useRef({});

  const updateAppt = useCallback(async function (data, {ntf, changingDoctor}) {
    dispatch &&
      dispatch({type: "UPDATE_APPOINTMENT", payload: data, changingDoctor});
    console.log("updateAppt");
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const updatPt = useCallback(async function (pid, payload, options) {
    dispatch && dispatch({type: "UPDATE_PATIENT", pid, payload, options});
  }, []);

  const newAppt = useCallback(async function (data, {ntf, updatePatient}) {
    dispatch &&
      dispatch({
        type: "NEW_APPOINTMENT",
        payload: data,
        updatePatient,
      });

    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const newFormSubmission = useCallback(async function (data, {ntf}) {
    dispatch &&
      dispatch({
        type: "NEW_FORM_SUBMISSION",
        payload: data,
      });
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);
  const completedForm = useCallback(async function ({ntf}) {
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const ccAdded = useCallback(async function ({ntf}) {
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  // One form of this patient's changed. The payload deliberately carries no
  // answers — only enough to know the list is stale — so this re-reads rather
  // than patching state from the wire.
  const formSubmissionChanged = useCallback(function (payload) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("brightlight:form-submission-changed", {detail: payload})
      );
    }
  }, []);

  useEffect(() => {
    if (room) {
      const rooms = tenant ? [room, `t:${tenant}:${room}`] : [room];
      // The session cookie travels with the connection, so the server can tell
      // who is listening and which clinic's rooms are theirs (S13).
      socketRef.current = io(url, {
        withCredentials: true,
        auth: {
          rooms,
        },
      });

      socketRef.current.on("update_appt", updateAppt);
      socketRef.current.on("update_pt", updatPt);
      socketRef.current.on("new_appt", newAppt);
      socketRef.current.on("new_form_submission", newFormSubmission);
      socketRef.current.on("completed_form", completedForm);
      socketRef.current.on("cc_added", ccAdded);
      socketRef.current.on("form_submission_changed", formSubmissionChanged);

      return () => {
        socketRef.current.off("update_appt");
        socketRef.current.off("new_appt");
        socketRef.current.off("update_pt");
        socketRef.current.off("new_form_submission");
        socketRef.current.off("completed_form");
        socketRef.current.off("cc_added");
        socketRef.current.off("form_submission_changed");
        socketRef.current.disconnect();
      };
    }
  }, [room, updateAppt]);

  return socketRef;
}

export function useChatSocket(
  url,
  rooms,
  patient,
  dispatch,
  dashDispatch,
  dashState
) {
  const socketRef = useRef({});

  const newMessge = useCallback(async function (data) {
    let {
      messageId,
      conversationId,
      senderId,
      messageType,
      message,
      attachment,
      timestamp,
    } = data;

    let msg = {
      messageId,
      conversationId,
      senderId,
      messageType,
      message,
      attachment,
      timestamp,
    };

    dispatch({
      type: "NEW_MESSAGE",
      payload: msg,
    });
  }, []);

  const userStatus = useCallback(async function ({userId, status}) {
    dispatch({
      type: "SET_USER_STATUS",
      payload: {userId, status},
    });
  }, []);

  const userCurrentStatus = useCallback(async function (status) {
    dispatch({
      type: "SET_USERS_CURRENT_STATUS",
      payload: status,
    });
  }, []);

  const newConversation = useCallback(async function (conversation) {
    dispatch({
      type: "NEW_CONVERSATION",
      payload: conversation,
    });

    dashDispatch({
      type: "UPDATE_PATIENT",
      payload: {
        chatsId: [
          ...new Set(
            [conversation.conversationId].concat(patient?.chatsId || [])
          ),
        ],
      },
    });
  }, []);

  const writingStatus = useCallback(async function ({chatId, userId}) {
    dispatch({
      type: "WRITING",
      payload: {chatId, userId},
    });
  }, []);

  // Handle message deletion
  const messageDeleted = useCallback(async function (data) {
    const { messageId, conversationId } = data;
    
    dispatch({
      type: "MESSAGE_DELETED",
      payload: { messageId, conversationId }
    });
  }, []);

  useEffect(() => {
    if (rooms || patient?.pid) {
      // The chat namespace takes the person and the clinic from the session
      // and refuses a socket without one (S18).
      socketRef.current = io(url, {
        withCredentials: true,
        auth: {
          rooms,
          userId: patient?.pid,
        },
      });

      socketRef.current.on("message", newMessge);
      socketRef.current.on("user-status", userStatus);
      socketRef.current.on("writing", writingStatus);
      socketRef.current.on("new-conversation", newConversation);
      socketRef.current.on("users-current-status", userCurrentStatus);
      socketRef.current.on("message-deleted", messageDeleted);

      return () => {
        socketRef.current.off("message", newMessge);
        socketRef.current.off("user-status", userStatus);
        socketRef.current.off("writing", writingStatus);
        socketRef.current.off("new-conversation", newConversation);
        socketRef.current.off("users-current-status", userCurrentStatus);
        socketRef.current.off("message-deleted", messageDeleted);
        socketRef.current.disconnect();
      };
    }
  }, [rooms]);

  return socketRef;
}

export function useWindowDimensions() {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return dimensions;
}
